import type { SerializedEditorState } from 'lexical';

import StoreORM from 'js/src/data/store-orm';
import { createEditorFromEditorState } from 'js/src/data/transcript/transcript-operations';
import type {
    LexicalEditorsDataType,
    SerializedTranscript,
} from 'js/src/data/transcript/transcript-types';
import type { GetRootState, RootState } from 'js/src/libs/RootState';

/** The saved transcripts a fetched video ships, keyed by scene-group id (video.transcripts). */
type SavedTranscripts = Record<string, Partial<SerializedTranscript> | SerializedEditorState>;

/**
 * Marks a state as describing a reference video rather than the one being edited.
 *
 * For the editing video a scene group with a transcript but no editor means hydration failed, and
 * reading cut-blind words would desync the agent from what its tools match against — so that stays
 * a throw. A reference video is read-only and may legitimately ship no saved transcripts at all,
 * so there the persisted words are the best available answer and must not fail the whole capture.
 */
export const REFERENCE_CAPTURE = Symbol.for('agent.referenceCapture');

/** A RootState tagged as describing a reference video. */
type ReferenceState = RootState & { [REFERENCE_CAPTURE]?: true };

/** Whether this state describes a reference video (see {@link REFERENCE_CAPTURE}). */
export function isReferenceCapture(state: RootState | undefined): boolean {
    return Boolean((state as ReferenceState | undefined)?.[REFERENCE_CAPTURE]);
}

/** The same editors table with every row removed, leaving the slice's shape intact. */
function blank(
    lexicalEditors: RootState['transcript']['lexical_editors'],
): RootState['transcript']['lexical_editors'] {
    const orm = new StoreORM<LexicalEditorsDataType, string>(lexicalEditors);
    orm.delete(orm.getAllIds());
    return orm.data;
}

/**
 * A reference video is loaded into an unmounted Luminary, so the session store has no transcript
 * editors for it — which means its pauses (SilenceNodes, read from the editor) can't be seen.
 *
 * Build headless Lexical editors from the video's saved transcript states (the same
 * createEditorFromEditorState the editor hydrates with) and overlay them onto the session state's
 * lexical_editors slice, producing a getState the capture pipeline can run. This makes a reference
 * video's transcript render through the exact same code as get_video_state — per-scene `covers:`
 * slices with pauses — rather than a separate, lesser view.
 *
 * The session state is spread, never mutated: StoreORM's Immutable persistent ops leave the live
 * lexical_editors untouched, and everything else the capture reads (notably the templates slice
 * that isSceneAnOverlay needs to classify overlays) is preserved as-is from the session state.
 *
 * A reference with no transcripts (a text-only video, say) still returns a getter carrying the
 * session state. The overlay is empty — there are no pauses to show — but the capture reads more
 * than transcripts from the store: color grading resolves against the `colorGrading` slice and the
 * template's named palettes against `templates`, neither of which has anything to do with the
 * reference's transcripts. Returning null here would drop the whole state and silently strip those
 * from the annotation. The state is tagged {@link REFERENCE_CAPTURE} so a group whose editor is
 * missing degrades to its persisted words instead of throwing.
 */
export function buildReferenceStateGetter(
    sessionState: RootState,
    transcripts: SavedTranscripts | undefined,
): GetRootState {
    const editors: LexicalEditorsDataType[] = [];
    for (const sceneGroupId of Object.keys(transcripts ?? {})) {
        const transcript = transcripts![sceneGroupId] as Partial<SerializedTranscript>;
        // A saved transcript is either a SerializedTranscript (carrying .editorState) or the raw
        // editor state itself — the same two shapes hydrateTranscripts accepts.
        const editorState = (transcript?.editorState ?? transcripts![sceneGroupId]) as
            | SerializedEditorState
            | undefined;
        if (!editorState) {
            continue;
        }
        editors.push({
            editor: createEditorFromEditorState(editorState),
            sceneGroupId,
            lexicalControlsItsOwnState: false,
        });
    }

    const lexicalEditors = sessionState.transcript?.lexical_editors;
    if (editors.length === 0 || !lexicalEditors) {
        // Nothing to overlay, so the session's own editors must not be left in place: they belong
        // to the video being edited, and a scene-group id shared with the reference would describe
        // the wrong video's transcript. Blank the slice and let the persisted words answer instead.
        const emptyState: ReferenceState = {
            ...sessionState,
            ...(lexicalEditors
                ? {
                      transcript: {
                          ...sessionState.transcript,
                          lexical_editors: blank(lexicalEditors),
                      },
                  }
                : {}),
            [REFERENCE_CAPTURE]: true,
        };
        return () => emptyState;
    }

    const orm = new StoreORM<LexicalEditorsDataType, string>(lexicalEditors);
    orm.delete(orm.getAllIds());
    editors.forEach((editor) => orm.pushJSItem(editor, 'sceneGroupId'));

    const referenceState: ReferenceState = {
        ...sessionState,
        transcript: { ...sessionState.transcript, lexical_editors: orm.data },
        [REFERENCE_CAPTURE]: true,
    };
    return () => referenceState;
}
